import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d66tg5aud6iuf6if0vpg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(data).toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-20 py-6 flex items-center justify-between">
          <h1 className="text-3xl font-serif tracking-wide" style={{ color: scrolled ? '#0F172A' : '#FFFFFF' }}>
            ZOR d.o.o.
          </h1>
          <div className="flex items-center gap-12">
            <button onClick={() => scrollToSection('home')} className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
              Home
            </button>
            <button onClick={() => scrollToSection('about')} className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
              About
            </button>
            <button onClick={() => scrollToSection('products')} className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
              Products
            </button>
            <button onClick={() => scrollToSection('features')} className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
              Features
            </button>
            <button onClick={() => scrollToSection('contact')} className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
              Contact
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/049b85f58042237186c5fc98aec8f8eb.jpg"
            alt="Modern technology background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative max-w-[1400px] mx-auto px-20 w-full">
          <div className="max-w-[55%]">
            <h2 className="text-7xl font-extrabold text-white leading-[1.1] mb-12">
              Modern iOS Applications<br />for Enhanced Security
            </h2>
            <p className="text-lg text-white/80 leading-relaxed max-w-[480px]">
              Expert team developing VPN and Cleaner solutions<br />
              Elevating mobile device security and user experience
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-gradient-to-br from-[#3E2723] to-[#1B0F0A] relative overflow-hidden">
        <div className="absolute top-20 right-20 text-[#8D6E63]/20 text-[120px] font-serif leading-none">"</div>
        <div className="max-w-[900px] mx-auto px-20 text-center relative z-10">
          <div className="inline-block px-6 py-2 rounded-full bg-[#8D6E63]/30 mb-7">
            <span className="text-xs uppercase tracking-widest text-white font-medium">ABOUT ZOR D.O.O.</span>
          </div>
          <h2 className="text-6xl font-serif text-white leading-tight mb-12">
            Crafting Excellence<br />in Mobile Solutions
          </h2>
          <div className="space-y-8 text-white/85 text-lg leading-relaxed">
            <p>
              ZOR d.o.o. is a dedicated team of experts specializing in the development of cutting-edge applications for the iOS platform. Our mission is to create products that not only meet but exceed user expectations in terms of functionality, security, and design.
            </p>
            <p>
              With years of experience in mobile development, we focus on delivering solutions that address real-world challenges. Our flagship products, including VPN and Cleaner applications, are designed with a user-first approach, ensuring seamless integration into daily mobile device usage.
            </p>
            <p>
              We believe in the power of innovation combined with meticulous attention to detail. Every line of code we write, every feature we implement, is crafted with precision and tested rigorously to ensure the highest standards of quality and performance.
            </p>
            <p className="text-white/95">
              Our commitment extends beyond development. We continuously evolve our products based on user feedback and emerging technologies, ensuring that our solutions remain at the forefront of the iOS ecosystem.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-36 bg-white">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="text-center mb-32">
            <div className="inline-block px-5 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 mb-6">
              <span className="text-xs uppercase tracking-widest text-white font-medium">OUR SOLUTIONS</span>
            </div>
            <h2 className="text-6xl font-bold text-[#1E293B]">Premium iOS Products</h2>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {/* VPN Product Card */}
            <div className="relative rounded-3xl bg-gradient-to-br from-[#0066FF] to-[#00D4FF] p-12 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="w-32 h-32 mx-auto mb-10 flex items-center justify-center">
                  <i className="ri-shield-check-line text-white/40 text-[120px]"></i>
                </div>
                <h3 className="text-3xl font-bold text-white mb-6 text-center">VPN Solution</h3>
                <ul className="space-y-4 text-white">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-base">Military-grade encryption</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-base">Global server network</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-base">Zero-log policy</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="relative rounded-3xl bg-gradient-to-br from-[#FF6B6B] to-[#FFB347] p-12 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="w-32 h-32 mx-auto mb-10 flex items-center justify-center">
                  <i className="ri-brush-line text-white/40 text-[120px]"></i>
                </div>
                <h3 className="text-3xl font-bold text-white mb-6 text-center">Device Cleaner</h3>
                <ul className="space-y-4 text-white">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-base">Smart storage optimization</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-base">Cache management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-base">Performance boost</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Coming Soon Card */}
            <div className="relative rounded-3xl bg-gradient-to-br from-[#64748B] to-[#94A3B8] p-12 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10 flex flex-col items-center justify-center h-full">
                <div className="w-32 h-32 mb-10 flex items-center justify-center animate-pulse">
                  <i className="ri-question-line text-white/40 text-[120px]"></i>
                </div>
                <h3 className="text-3xl font-bold text-white mb-4 text-center">Coming Soon</h3>
                <p className="text-lg text-white/90 text-center">Next Innovation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-36 bg-gradient-to-br from-[#FFF8F0] to-[#FFE8D6]">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="flex flex-col justify-center mb-32">
            <div className="text-xs uppercase tracking-widest text-[#FF6B35] font-semibold mb-5">EXPERTISE</div>
            <h2 className="text-5xl font-bold text-[#2C1810] leading-tight mb-8">
              Building the Future of iOS Security
            </h2>
            <div className="space-y-6 text-gray-700 text-base leading-relaxed max-w-[560px]">
              <p>
                Our team combines deep technical expertise with a passion for creating exceptional user experiences. We understand the complexities of iOS development and leverage the latest technologies to deliver robust, scalable solutions.
              </p>
              <p>
                Every project we undertake follows a rigorous development process that emphasizes security, performance, and usability. From initial concept to final deployment, we maintain the highest standards of code quality and testing.
              </p>
              <p>
                We are committed to continuous improvement and innovation. Our dedication to excellence has earned us the trust of users worldwide who rely on our applications to protect their privacy and optimize their devices.
              </p>
            </div>
          </div>

          {/* Development Principles */}
          <div className="text-center mb-24">
            <h3 className="text-5xl font-bold text-[#1E293B]">Our Development Principles</h3>
          </div>

          <div className="grid grid-cols-4 gap-7">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-shield-star-line text-white text-4xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-4">Security First</h4>
              <p className="text-sm text-gray-600 leading-relaxed w-[90%] mx-auto">
                We prioritize user privacy and data protection in every aspect of our development process, implementing industry-leading security standards.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-rocket-line text-white text-4xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-4">Innovation</h4>
              <p className="text-sm text-gray-600 leading-relaxed w-[90%] mx-auto">
                Constantly exploring new technologies and methodologies to deliver cutting-edge solutions that stay ahead of the curve.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-user-heart-line text-white text-4xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-4">User-Centric</h4>
              <p className="text-sm text-gray-600 leading-relaxed w-[90%] mx-auto">
                Designing intuitive interfaces and seamless experiences that put users at the center of everything we create.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mx-auto mb-8">
                <i className="ri-code-s-slash-line text-white text-4xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-4">Quality Code</h4>
              <p className="text-sm text-gray-600 leading-relaxed w-[90%] mx-auto">
                Writing clean, maintainable code with comprehensive testing to ensure reliability and performance across all devices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-white border-t border-gray-200">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-[#1E293B] mb-4">Get in Touch</h2>
            <p className="text-lg text-gray-600">We're here to answer your questions</p>
          </div>

          <div className="grid grid-cols-[45%_55%] gap-16">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">ADDRESS</div>
                <div className="text-lg text-[#1E293B]">Donje Mrzlo Polje Mreznicko 122</div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">CITY</div>
                <div className="text-lg text-[#1E293B]">Duga Resa</div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">ZIP CODE</div>
                <div className="text-lg text-[#1E293B]">47250</div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">COUNTRY</div>
                <div className="text-lg text-[#1E293B]">Croatia</div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">EMAIL</div>
                <a href="mailto:info@zor-doo.com" className="text-lg text-blue-600 hover:text-blue-700 transition-colors cursor-pointer">
                  info@zor-doo.com
                </a>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">PHONE</div>
                <a href="tel:+18302514755" className="text-lg text-[#1E293B] hover:text-gray-700 transition-colors cursor-pointer">
                  +1 8302514755
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 rounded-2xl p-10">
              <h3 className="text-2xl font-bold text-[#1E293B] mb-6">Send us a message</h3>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="Your message (max 500 characters)"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")', backgroundSize: '60px 60px' }}></div>
        </div>
        <div className="max-w-[1400px] mx-auto px-20 relative z-10">
          <div className="grid grid-cols-[30%_40%_30%] gap-8 items-start">
            <div>
              <h3 className="text-3xl font-serif text-white mb-6">ZOR d.o.o.</h3>
              <p className="text-sm text-gray-400 mb-8">Excellence in iOS Development</p>
              <p className="text-xs text-gray-500">© 2025 ZOR d.o.o. All rights reserved</p>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-400 leading-relaxed w-[80%] mx-auto">
                Specialized in VPN and Cleaner solutions for iOS platform
              </p>
            </div>

            <div className="text-right">
              <p className="text-sm text-gray-400 mb-2">Made with precision</p>
              <p className="text-sm text-white font-medium">Croatia, EU</p>
            </div>
          </div>

          <div className="mt-16 pt-10 border-t border-white/10 text-center">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs text-gray-500 hover:text-gray-400 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
